import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { motion } from 'framer-motion';
import Swal from 'sweetalert2';
import {
  FiGift,
  FiCalendar,
  FiActivity,
  FiSave,
  FiDollarSign,
  FiCheckCircle,
  FiCamera,
  FiUpload,
  FiAlertCircle,
  FiTrash2
} from 'react-icons/fi';
import { FaRegImage } from 'react-icons/fa';

const schema = yup.object().shape({
  title: yup.string().required('Title is required').min(3, 'Title must be at least 3 characters'),
  requiredBookings: yup
    .number()
    .typeError('Number of bookings must be a number')
    .positive('Number of bookings must be positive')
    .required('Number of bookings is required')
});

const AddNewReward = () => {
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: '',
      description: '',
      requiredBookings: '',
      rewardValue: '',
      rewardType: 'gift',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      status: 'active',
      notes: ''
    }
  });

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          title: "File Too Large",
          text: "Image size should be less than 5MB",
          icon: "warning",
          confirmButtonColor: "#6366f1"
        });
        return;
      }
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          title: "File Too Large",
          text: "Image size should be less than 5MB",
          icon: "warning",
          confirmButtonColor: "#6366f1"
        });
        return;
      }
      setImagePreview(URL.createObjectURL(file));
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      document.getElementById('reward-image').files = dataTransfer.files;
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      let imageUrl = '';

      const fileInput = document.getElementById('reward-image');
      if (fileInput && fileInput.files && fileInput.files[0]) {
        const formData = new FormData();
        formData.append("file", fileInput.files[0]);
        formData.append("upload_preset", "prayagproperties");

        const cloudinaryResponse = await fetch(
          "https://api.cloudinary.com/v1_1/djmyigser/image/upload",
          {
            method: "POST",
            body: formData,
          }
        );

        const cloudinaryData = await cloudinaryResponse.json();
        if (!cloudinaryData.secure_url) {
          throw new Error("Failed to upload image to Cloudinary");
        }
        imageUrl = cloudinaryData.secure_url;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/rewards`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...data,
          image: imageUrl
        })
      });

      const result = await response.json();

      Swal.fire({
        title: 'Success!',
        text: 'Reward created successfully.',
        icon: 'success',
        confirmButtonColor: '#6366f1',
        timer: 2000,
        timerProgressBar: true,
        background: '#ffffff'
      });
      reset();
      setImagePreview(null);
    } catch (error) {
      console.error('Error:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to create reward. Please try again.',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        background: '#ffffff'
      });
    } finally {
      setLoading(false);
    }
  };

  const watchedTitle = watch("title");

  const inputClasses = 'w-full px-4 py-3 rounded-xl border transition-all duration-200 outline-none bg-gray-50/50 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200';
  const labelClasses = 'block text-sm font-semibold text-gray-700 mb-2';

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10'>
      <div className='container mx-auto px-4 max-w-3xl'>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className='text-center mb-8'
        >
          <div className='inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg mb-4 mx-auto'>
            <FiGift className='w-8 h-8 text-white' />
          </div>
          <h1 className='text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent'>
            Add New Reward
          </h1>
          <p className='text-gray-500 mt-2 text-sm'>
            Create exciting rewards for your partners
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className='bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100'
        >
          <div className='bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5'>
            <div className='flex items-center gap-3'>
              <div className='p-2 bg-white/20 rounded-xl'>
                <FiGift className='w-5 h-5 text-white' />
              </div>
              <div>
                <h2 className='text-white font-semibold text-xl'>Reward Information</h2>
                <p className='text-indigo-100 text-sm mt-0.5'>Fill reward details</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className='p-6 space-y-5'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
              <div className='md:col-span-2'>
                <label className={labelClasses}>
                  Reward Title <span className='text-red-500'>*</span>
                </label>
                <div className='relative'>
                  <FiGift className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                  <input
                    {...register('title')}
                    className={`${inputClasses} pl-10 ${errors.title ? 'border-red-500' : ''}`}
                    placeholder='e.g., Goa Trip, Smartphone'
                  />
                </div>
                {errors.title && (
                  <p className='text-xs text-red-500 mt-1 flex items-center gap-1'>
                    <span>{errors.title.message}</span>
                  </p>
                )}
              </div>
              <div>
                <label className={labelClasses}>
                  Required Bookings <span className='text-red-500'>*</span>
                </label>
                <div className='relative'>
                  <FiActivity className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                  <input
                    type='number'
                    {...register('requiredBookings')}
                    className={`${inputClasses} pl-10 ${errors.requiredBookings ? 'border-red-500' : ''}`}
                    placeholder='5'
                  />
                </div>
                {errors.requiredBookings && (
                  <p className='text-xs text-red-500 mt-1 flex items-center gap-1'>
                    <span>{errors.requiredBookings.message}</span>
                  </p>
                )}
              </div>
              <div>
                <label className={labelClasses}>Reward Type</label>
                <Controller
                  name='rewardType'
                  control={control}
                  render={({ field }) => (
                    <select {...field} className={`${inputClasses} cursor-pointer`}>
                      <option value='gift'>Gift</option>
                      <option value='trip'>Trip</option>
                      <option value='vehicle'>Vehicle</option>
                      <option value='cash'>Cash</option>
                      <option value='other'>Other</option>
                    </select>
                  )}
                />
              </div>
              <div>
                <label className={labelClasses}>Reward Value</label>
                <div className='relative'>
                  <FiDollarSign className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                  <input
                    type='number'
                    {...register('rewardValue')}
                    className={`${inputClasses} pl-10`}
                    placeholder='0'
                  />
                </div>
              </div>
              <div>
                <label className={labelClasses}>Start Date</label>
                <div className='relative'>
                  <FiCalendar className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                  <input
                    type='date'
                    {...register('startDate')}
                    className={`${inputClasses} pl-10`}
                  />
                </div>
              </div>
              <div>
                <label className={labelClasses}>End Date</label>
                <div className='relative'>
                  <FiCalendar className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                  <input
                    type='date'
                    {...register('endDate')}
                    className={`${inputClasses} pl-10`}
                  />
                </div>
              </div>

              {/* Image Upload Field */}
              <div className='md:col-span-2'>
                <label className={labelClasses}>Upload Reward Image</label>
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
                    isDragging 
                      ? 'border-indigo-500 bg-indigo-50' 
                      : imagePreview 
                        ? 'border-emerald-300 bg-emerald-50/30'
                        : 'border-gray-300 hover:border-indigo-400 hover:bg-indigo-50/30'
                  }`}
                  onClick={() => document.getElementById('reward-image').click()}
                >
                  <input
                    id="reward-image"
                    type="file"
                    accept="image/*"
                    {...register("certificateImage")}
                    onChange={handleImageChange}
                    className="hidden"
                  />
                  
                  {imagePreview ? (
                    <div className="flex flex-col items-center">
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-40 h-40 object-cover rounded-xl shadow-lg border-4 border-white"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setImagePreview(null);
                            document.getElementById('reward-image').value = '';
                          }}
                          className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full shadow-md hover:bg-red-600 transition"
                        >
                          <FiTrash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <p className="text-sm text-emerald-600 mt-3 flex items-center gap-1">
                        <FiCheckCircle className="w-4 h-4" />
                        Image uploaded successfully
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center mx-auto mb-3">
                        <FiCamera className="w-6 h-6 text-indigo-500" />
                      </div>
                      <p className="text-gray-600 font-medium">Click or drag to upload</p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF up to 5MB</p>
                    </div>
                  )}
                </div>
              </div>

              <div className='md:col-span-2'>
                <label className={labelClasses}>Description</label>
                <textarea
                  rows={3}
                  {...register('description')}
                  className={`${inputClasses}`}
                  placeholder='Describe the reward...'
                />
              </div>
              <div>
                <label className={labelClasses}>Notes</label>
                <textarea
                  rows={2}
                  {...register('notes')}
                  className={`${inputClasses}`}
                  placeholder='Any additional notes...'
                />
              </div>
              <div>
                <label className={labelClasses}>Status</label>
                <Controller
                  name='status'
                  control={control}
                  render={({ field }) => (
                    <select {...field} className={`${inputClasses} cursor-pointer`}>
                      <option value='active'>✓ Active</option>
                      <option value='inactive'>✗ Inactive</option>
                    </select>
                  )}
                />
              </div>
            </div>

            {/* Preview Section */}
            {watchedTitle && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100"
              >
                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <FiCheckCircle className="w-4 h-4 text-emerald-600" />
                  Preview
                </h4>
                <div className="flex items-center gap-4">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="w-16 h-16 rounded-lg object-cover border-2 border-indigo-300" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                      <FaRegImage className="w-6 h-6 text-indigo-500" />
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-gray-800">{watchedTitle}</p>
                    <p className="text-xs text-gray-500">Reward</p>
                  </div>
                </div>
              </motion.div>
            )}

            <div className='pt-4 border-t border-gray-100'>
              <button
                type='submit'
                disabled={loading}
                className='w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {loading ? (
                  <>
                    <div className='w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin' />
                    Creating Reward...
                  </>
                ) : (
                  <>
                    <FiSave className='w-4 h-4' />
                    Create Reward
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default AddNewReward;
